import type { Identifiable } from '@seddi/core';
import log from 'loglevel';

import { assert } from '../core/assert';
import type { ResourceKind, ResourceStatus } from './resourceEnum';

type ResourceManagerItem<T> = { ref: number; status: ResourceStatus; value?: T };

export class ResourceManager<T> {
  // ugly way of avoiding this class to be an EventTarget
  public onLoad?: (id: string, type: ResourceKind) => void;

  protected readonly deferred: Map<string, Identifiable>;
  protected readonly map: Map<string, ResourceManagerItem<T>>;

  constructor() {
    this.deferred = new Map();
    this.map = new Map();
  }

  dispose() {}

  get(id: string): T | undefined {
    const res = this.map.get(id);
    assert(res, `ResourceManager.get: ${id} not found.`);
    return res.value;
  }

  getRef(id: string): T | undefined {
    const res = this.map.get(id);
    assert(res, `ResourceManager.getRef: ${id} not found.`);
    res.ref += 1;
    return res.value;
  }

  find(id: string): T | undefined {
    const res = this.map.get(id);

    if (res) {
      return res.value;
    }

    return undefined;
  }

  findRef(id: string): T | undefined {
    const res = this.map.get(id);

    if (res) {
      res.ref += 1;
      return res.value;
    }

    return undefined;
  }

  retRef(id: string) {
    const res = this.map.get(id);
    assert(res, `ResourceManager.retRef: ${id} not found.`);
    res.ref -= 1;

    if (res.ref < 0) {
      log.warn(`ResourceManager.retRef: ${id} count error.`);
    }
  }

  commit() {
    this.deferred.forEach((descriptor) => {
      this.loadAsync(descriptor);
    });
    this.deferred.clear();
  }

  has(id: string): boolean {
    return !!this.map.get(id);
  }

  hasDeferred(id: string): boolean {
    return !!this.deferred.get(id);
  }

  hasRequest(id: string): boolean {
    return this.hasDeferred(id) || this.has(id);
  }

  loadAsync(_descriptor: Identifiable): Promise<any> {
    log.warn(`ResourceManager.loadAsync not implemented.`);
    return Promise.resolve();
  }

  loadDeferred(descriptor: Identifiable) {
    const prev = this.deferred.get(descriptor.id);

    if (prev) {
      log.warn(`ResourceManager.loadDeferred: ${descriptor.id} is not new.`);
      return;
    }

    this.deferred.set(descriptor.id, descriptor);
  }

  move(id: string, value: T) {
    const exists = this.map.has(id);
    assert(exists, `ResourceManager.move: ${id} is not new.`);
    const res: ResourceManagerItem<T> = { ref: 0, status: 'ready', value };
    this.map.set(id, res);
    res.value = value;
  }

  protected create(id: string): ResourceManagerItem<T> {
    const prev = this.map.get(id);
    assert(!prev, `ResourceManager.create: ${id} is not new.`);
    const res: ResourceManagerItem<T> = { ref: 0, status: 'unknown' };
    this.map.set(id, res);

    return res;
  }
}
